import React, { useEffect, useRef, useState } from "react";
import { X, Mail, Copy, Check, Twitter, Facebook, Smile, Send, Printer, Heart } from "lucide-react";
import { toast } from "sonner";

const PRESET_MESSAGES = [
  "yo, I found this sick P2P app from like 2003. you gotta check it out!! 100% virus free (they scanned everything). also there's free BANGERS: <URL>",
  "OMG you have to see this, it's like LimeWire but they PROMISE there's no malware this time: <URL>   bring your dial-up 🔊",
  "drop everything. go here RIGHT NOW. ask me no questions. <URL>   - ur best friend",
  "dude do u remember when ur dad got a virus downloading Metallica on Napster? well that would NEVER happen here because LemonShield: <URL>",
  "this is the mixtape generator we needed in 6th grade. put my name down for the burned CD-R: <URL>",
];

const HANDLES = [
  "xXxRaVeKiNg2003xXx", "napsteRnightmare", "dial_up_dylan",
  "lemonsquad_luis", "Tha_RealSlimJeanette", "msn_brandon07",
];

/**
 * "Tell a Friend" modal styled as a 2003 Outlook Express / Hotmail compose
 * window. Lets the user copy an invite link, auto-generate a goofy 2003-style
 * email body, share on Twitter/Facebook/WhatsApp, or open a mailto: draft.
 */
export default function TellAFriendModal({ onClose, shareUrl = "https://lemonwire.fm/" }) {
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [handle] = useState(() => HANDLES[Math.floor(Math.random() * HANDLES.length)]);
  const [msgIdx, setMsgIdx] = useState(() => Math.floor(Math.random() * PRESET_MESSAGES.length));
  const [subject, setSubject] = useState("FW: FW: RE: FW: you gotta see this site!!! 🍋🔌");
  const [message, setMessage] = useState(PRESET_MESSAGES[0].replace("<URL>", shareUrl));
  const [friendEmail, setFriendEmail] = useState("");
  const modalRef = useRef(null);

  // Keep message in sync when user cycles presets
  useEffect(() => {
    setMessage(PRESET_MESSAGES[msgIdx].replace("<URL>", shareUrl));
  }, [msgIdx, shareUrl]);

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* ignored */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${subject}\n\n${message}`); } catch { /* ignored */ }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 1600);
  };

  const sendEmail = () => {
    const params = new URLSearchParams({ subject, body: message });
    window.location.href = `mailto:${encodeURIComponent(friendEmail)}?${params.toString()}`;
  };

  const shareTwitter = () => {
    const text = `Found this sick 2003 P2P MP3 fansite. 100% virus-free (they ScaNned every file)  🍋🔌   ${shareUrl}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const shareOrkut = async () => {
    // Orkut shut down in 2014 — lean into the gag. Copy a scrap-style message
    // to the clipboard and show a nostalgic "servers offline" toast.
    const scrap = `Oi amigo(a)! 🌺

Passando no seu scrap p/ mandar um recadinho especial...

Vc TEM que conhecer esse site novo q td mundo ta falando: ${shareUrl}

É tipo o LimeWire mas SEM virus prometo! Tem Evanescence, Britney, Linkin Park, td q vc ama!

Deixa um recado no meu scrap depois! Bjinhos ✨💫

— enviado via LemonWire.exe`;
    try { await navigator.clipboard.writeText(scrap); } catch { /* ignored */ }
    toast("Orkut servers are offline (since Sept. 30, 2014) 😢", {
      description: "Scrap copied to your clipboard! Paste it into literally any modern social network instead.",
      duration: 4200,
    });
  };

  return (
    <div
      data-testid="tell-friend-modal"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="ie-chrome flex flex-col overflow-hidden"
        style={{ width: 620, maxWidth: "96vw", maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title bar — Outlook-purple vibe */}
        <div
          className="h-[28px] flex items-center justify-between px-2 select-none border-b border-black"
          style={{
            background: "linear-gradient(to bottom, #0A2C7A 0%, #1A46B6 15%, #2F66D4 45%, #5988E4 80%, #0A2C7A 100%)",
            color: "white",
            textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
          }}
        >
          <div className="flex items-center gap-2 text-[12.5px] font-bold">
            <Mail size={13} />
            New Message — LemonWire Invite
          </div>
          <div className="flex items-center gap-[2px]">
            <WinBtn color="blue"><span style={{ fontSize: 9, fontWeight: 900 }}>_</span></WinBtn>
            <WinBtn color="blue"><span style={{ fontSize: 8 }}>□</span></WinBtn>
            <WinBtn color="red" onClick={onClose} data-testid="tell-close"><X size={10} strokeWidth={4} /></WinBtn>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1 bg-[#ECE9D8] border-b border-[#9C9A7E]">
          <ToolbarBtn onClick={sendEmail} data-testid="tell-send" title="Send via your email client">
            <Send size={12} /> <span className="ml-1 font-bold">Send</span>
          </ToolbarBtn>
          <ToolbarBtn onClick={copyEmail} title="Copy the full email text">
            {emailCopied ? <Check size={12} className="text-[#1F8020]" /> : <Copy size={12} />}
            <span className="ml-1">{emailCopied ? "Copied!" : "Copy text"}</span>
          </ToolbarBtn>
          <div className="w-px h-5 bg-[#9C9A7E] mx-1" />
          <ToolbarBtn onClick={() => window.print()}><Printer size={12} /><span className="ml-1">Print</span></ToolbarBtn>
          <div className="ml-auto text-[10px] text-[#555] italic pr-1">
            From: <b>{handle}@lemonwire.fm</b>
          </div>
        </div>

        {/* Form */}
        <div className="p-3 bg-white flex flex-col gap-2 tell-friend-body overflow-y-auto xp-scroll">
          <Row label="To:">
            <input
              data-testid="tell-to"
              type="email"
              value={friendEmail}
              onChange={(e) => setFriendEmail(e.target.value)}
              placeholder="friend@hotmail.com"
              className="flex-1 bevel-in bg-white px-2 py-1 text-[12px] font-mono focus:outline-none"
            />
            <button
              type="button"
              className="bevel-btn px-2 py-[3px] text-[11px] shrink-0"
              onClick={() => setFriendEmail("best.friend.4ever@aol.com")}
              title="Auto-fill demo"
            >
              A/S/L?
            </button>
          </Row>
          <Row label="Subject:">
            <input
              data-testid="tell-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 bevel-in bg-white px-2 py-1 text-[12px] focus:outline-none"
            />
          </Row>

          {/* Message area */}
          <div className="mt-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-[#555] font-bold">Message:</span>
              <button
                type="button"
                data-testid="tell-cycle-msg"
                className="bevel-btn px-2 py-[2px] text-[10.5px]"
                onClick={() => setMsgIdx((i) => (i + 1) % PRESET_MESSAGES.length)}
              >
                🎲 Try another line
              </button>
              <span className="ml-auto text-[9px] text-[#999]">
                {msgIdx + 1} / {PRESET_MESSAGES.length}
              </span>
            </div>
            <textarea
              data-testid="tell-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full bevel-in bg-white px-2 py-1.5 text-[12.5px] focus:outline-none resize-none"
              style={{ fontFamily: "Tahoma, Verdana, sans-serif" }}
            />
          </div>

          {/* Invite link */}
          <div className="bevel-in bg-[#FFFDE7] p-2 flex items-center gap-2 mt-1 tell-friend-link">
            <Heart size={12} className="text-[#E81123] shrink-0" />
            <span className="text-[11px] text-[#5A4500] font-bold shrink-0">Direct link:</span>
            <input
              readOnly
              value={shareUrl}
              data-testid="tell-link"
              className="flex-1 bg-transparent text-[12px] font-mono text-[#003c9e] focus:outline-none min-w-0"
              onFocus={(e) => e.target.select()}
            />
            <button
              data-testid="tell-copy-link"
              onClick={copyLink}
              className={`${copied ? "bevel-btn-green" : "bevel-btn"} px-2 py-1 text-[11px] flex items-center gap-1 shrink-0`}
            >
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          {/* Share buttons */}
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <span className="text-[11px] text-[#555]">or share via:</span>
            <button
              data-testid="tell-share-twitter"
              onClick={shareTwitter}
              className="bevel-btn px-2 py-1 text-[11px] flex items-center gap-1"
              style={{ background: "linear-gradient(to bottom, #8DD1F8, #1DA1F2 60%, #0D7BB8)" }}
            >
              <Twitter size={11} className="text-white" /> <span className="text-white font-bold">Tweet it</span>
            </button>
            <button
              data-testid="tell-share-facebook"
              onClick={shareFacebook}
              className="bevel-btn px-2 py-1 text-[11px] flex items-center gap-1"
              style={{ background: "linear-gradient(to bottom, #5F81C7, #1877F2 60%, #0B5FD9)" }}
            >
              <Facebook size={11} className="text-white" /> <span className="text-white font-bold">Post to Wall</span>
            </button>
            <button
              data-testid="tell-share-orkut"
              onClick={shareOrkut}
              title="Post to Orkut (servers may be offline since 2014 — we'll copy the scrap instead)"
              className="bevel-btn px-2 py-1 text-[11px] flex items-center gap-1"
              style={{ background: "linear-gradient(to bottom, #FFB3D9, #ED2590 55%, #A8125D)" }}
            >
              <Smile size={11} className="text-white" /> <span className="text-white font-bold">Orkut Scrap</span>
            </button>
          </div>
        </div>

        {/* Status bar */}
        <div
          className="px-2 py-1 flex items-center justify-between text-[11px] border-t-2 border-[#808080] tell-friend-status"
          style={{ background: "linear-gradient(to bottom, #ECE9D8, #D4D0BF)" }}
        >
          <span className="flex items-center gap-1 text-[#555]">
            <Mail size={10} />
            Draft · {message.length} characters · {Math.ceil(message.length / 32)} blinks/sec
          </span>
          <span className="text-[10px] text-[#555] italic">LemonMail™ v4.2</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-[#555] font-bold w-[68px] shrink-0">{label}</span>
      {children}
    </div>
  );
}

function ToolbarBtn({ children, ...props }) {
  return (
    <button
      {...props}
      className="bevel-btn px-2 py-[3px] text-[11px] flex items-center hover:brightness-110"
    >
      {children}
    </button>
  );
}

function WinBtn({ color, children, ...props }) {
  const bg = color === "red"
    ? "bg-gradient-to-b from-[#F58D85] via-[#E81123] to-[#8B0A14]"
    : "bg-gradient-to-b from-[#7BB3FF] via-[#247DF6] to-[#003c9e]";
  return (
    <button
      {...props}
      className={`w-[22px] h-[19px] flex items-center justify-center text-white rounded-[2px] ${bg} hover:brightness-110`}
    >
      {children}
    </button>
  );
}
